import { resolveVersionForwardChanges } from "@/apis/forward/route";
import queries from "@/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { VersionForwardChangeResponse } from "@/apis/forward/types";

/**
 * 버전 업데이트로 달라졌고 아직 확인하지 않은 표의 id.
 *
 * 버전 포워드로 올라오지 않은 워크북이면 빈 집합이다. SNB 가 어느 주석에 변경이 있는지
 * 표시하는 데 쓴다.
 */
export const useUnresolvedChangedTableIds = (
  workbookId: string | null | undefined,
): Set<string> => {
  const { data } = useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.versionForwardChanges(),
    enabled: Boolean(workbookId),
  });

  return useMemo(() => {
    const ids = new Set<string>();
    if (!data) return ids;
    for (const item of data.items) {
      if (item.resolved) continue;
      ids.add(item.tableId);
    }
    return ids;
  }, [data]);
};

/**
 * 주석을 열어봤을 때 그 주석의 변경 알림을 내린다.
 *
 * 화면에서는 즉시 지우고, 실패하면 되돌린다 — 알림이 늦게 사라지면 방금 누른 것이
 * 먹혔는지 알 수 없다.
 */
export const useResolveChangedTables = (
  workbookId: string | null | undefined,
) => {
  const queryClient = useQueryClient();
  const queryKey = queries
    .workbook(workbookId ?? "")
    ._ctx.versionForwardChanges().queryKey;

  return useMutation({
    mutationFn: (tableIds: string[]) =>
      resolveVersionForwardChanges(workbookId!, tableIds),
    onMutate: async (tableIds) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<VersionForwardChangeResponse>(queryKey);
      queryClient.setQueryData<VersionForwardChangeResponse>(
        queryKey,
        (old) =>
          old && {
            items: old.items.map((item) =>
              tableIds.includes(item.tableId)
                ? { ...item, resolved: true }
                : item,
            ),
          },
      );
      return { previous };
    },
    onError: (_error, _tableIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
